import { Fragment, useState, type ReactNode } from 'react';
import { Etiquette, Montant, Vide } from '../../composants/communs.tsx';
import { LIBELLE_CANAL, type Canal } from '../produits/types.ts';
import { calculerTotaux, seuilAtteint, totalLigne } from './calculs.ts';
import type { LignePanierVente } from './vente-types.ts';

/** Panier éditable et ses totaux — RG-002 à RG-004, RG-035. */
export function PanierTable({
  panier,
  canal,
  remisePied,
  onModifierLigne,
  onRetirerLigne,
  onRemisePied,
  onVider,
}: {
  panier: LignePanierVente[];
  canal: Canal;
  remisePied: number;
  onModifierLigne: (produitId: string, champs: Partial<LignePanierVente>) => void;
  onRetirerLigne: (produitId: string) => void;
  onRemisePied: (valeur: number) => void;
  onVider: () => void;
}): ReactNode {
  const totaux = calculerTotaux(panier, remisePied);

  // Capture d'IMEI repliée par défaut : la plupart des articles (vivres,
  // accessoires…) n'en ont pas, et quatre champs par unité affichés pour
  // rien noient la ligne. État purement local : décocher efface les IMEI
  // déjà saisis plutôt que de les garder cachés sans que personne le sache.
  const [avecImei, setAvecImei] = useState<Set<string>>(new Set());

  return (
    <div className="vente-section">
      <div className="vente-section__tete">
        <h2>
          Panier <span className="entete__sous">{panier.length} article{panier.length > 1 ? 's' : ''}</span>
        </h2>
        {panier.length > 0 && (
          <button type="button" className="bouton--discret" style={{ color: 'var(--brick)' }} onClick={onVider}>
            Vider le panier
          </button>
        )}
      </div>

      {panier.length === 0 ? (
        <Vide texte="Le panier est vide. Cherchez un produit ci-dessus." />
      ) : (
        <div className="tableau-defilant">
          <table>
            <thead>
              <tr>
                <th>Article</th>
                <th>Quantité</th>
                <th>Prix unit.</th>
                <th>Remise</th>
                <th>Total</th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {panier.map((ligne) => {
                const sousSeuil = !seuilAtteint(ligne.quantite, ligne.quantiteMinimale);
                const prixModifie = ligne.prixUnitaire !== ligne.prixCatalogue;
                const imeiActif = avecImei.has(ligne.produitId);
                const imeisRemplis = ligne.imeis.filter(
                  (paire) => paire.imei.trim() !== '' || paire.imei2.trim() !== '',
                ).length;
                return (
                  <Fragment key={ligne.produitId}>
                  <tr>
                    <td>
                      {ligne.designation}
                      {sousSeuil && (
                        <>
                          {' '}
                          <Etiquette
                            ton="ambre"
                            texte={`seuil ${LIBELLE_CANAL[canal]} : ${String(ligne.quantiteMinimale)}`}
                          />
                        </>
                      )}
                    </td>
                    <td>
                      <div className="panier-ligne-quantite">
                        <button
                          type="button"
                          className="bouton--discret"
                          aria-label={`Diminuer la quantité — ${ligne.designation}`}
                          disabled={ligne.quantite <= 1}
                          onClick={() => {
                            onModifierLigne(ligne.produitId, { quantite: Math.max(1, ligne.quantite - 1) });
                          }}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={ligne.quantite}
                          onChange={(evenement) => {
                            onModifierLigne(ligne.produitId, {
                              quantite: Math.max(1, Number(evenement.target.value) || 1),
                            });
                          }}
                        />
                        <button
                          type="button"
                          className="bouton--discret"
                          aria-label={`Augmenter la quantité — ${ligne.designation}`}
                          onClick={() => {
                            onModifierLigne(ligne.produitId, { quantite: ligne.quantite + 1 });
                          }}
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        style={{ maxWidth: 96 }}
                        value={ligne.prixUnitaire}
                        onChange={(evenement) => {
                          onModifierLigne(ligne.produitId, {
                            prixUnitaire: Math.max(1, Number(evenement.target.value) || 1),
                          });
                        }}
                      />
                      {prixModifie && (
                        <>
                          {' '}
                          <Etiquette
                            ton="ambre"
                            texte={`catalogue ${String(ligne.prixCatalogue)}`}
                          />
                        </>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        style={{ maxWidth: 96 }}
                        value={ligne.remiseLigne}
                        onChange={(evenement) => {
                          onModifierLigne(ligne.produitId, {
                            remiseLigne: Math.max(0, Number(evenement.target.value) || 0),
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Montant valeur={totalLigne(ligne)} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`bouton--discret badge-imei${imeisRemplis > 0 ? ' badge-imei--rempli' : ''}`}
                        aria-pressed={imeiActif}
                        aria-label={`Saisir les IMEI — ${ligne.designation}`}
                        onClick={() => {
                          setAvecImei((actuel) => {
                            const suivant = new Set(actuel);
                            if (suivant.has(ligne.produitId)) {
                              suivant.delete(ligne.produitId);
                            } else {
                              suivant.add(ligne.produitId);
                            }
                            return suivant;
                          });
                        }}
                      >
                        IMEI {imeisRemplis}/{ligne.quantite}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="bouton--discret bouton--danger bouton--icone"
                        aria-label={`Retirer ${ligne.designation} du panier`}
                        onClick={() => {
                          onRetirerLigne(ligne.produitId);
                        }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                  {imeiActif && (
                    <tr className="ligne-imei">
                      <td colSpan={7} style={{ paddingTop: 0 }}>
                        <table className="tableau-imei">
                          <thead>
                            <tr>
                              <th>Unité</th>
                              <th>IMEI 1</th>
                              <th>IMEI 2 (double SIM)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: ligne.quantite }, (_, index) => {
                              const paire = ligne.imeis[index] ?? { imei: '', imei2: '' };
                              return (
                                <tr key={index}>
                                  <td>n° {index + 1}</td>
                                  <td>
                                    <input
                                      aria-label={`IMEI unité ${String(index + 1)} — ${ligne.designation}`}
                                      placeholder="15 chiffres"
                                      value={paire.imei}
                                      onChange={(evenement) => {
                                        const imeis = [...ligne.imeis];
                                        imeis[index] = { ...paire, imei: evenement.target.value };
                                        onModifierLigne(ligne.produitId, { imeis });
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      aria-label={`IMEI2 unité ${String(index + 1)} (double SIM) — ${ligne.designation}`}
                                      placeholder="facultatif"
                                      value={paire.imei2}
                                      onChange={(evenement) => {
                                        const imeis = [...ligne.imeis];
                                        imeis[index] = { ...paire, imei2: evenement.target.value };
                                        onModifierLigne(ligne.produitId, { imeis });
                                      }}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <p className="entete__sous" style={{ marginTop: 4 }}>
                          IMEI facultatifs — scannez la boîte ou saisissez-les. Ils seront imprimés sur la facture.
                        </p>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="champ" style={{ maxWidth: 200, marginTop: 12 }}>
        <label htmlFor="vente-remise-pied">Remise (pied de facture)</label>
        <input
          id="vente-remise-pied"
          type="number"
          min={0}
          step={1}
          value={remisePied}
          onChange={(evenement) => {
            onRemisePied(Math.max(0, Number(evenement.target.value) || 0));
          }}
        />
      </div>

      <div className="pile" style={{ gap: 4, marginTop: 12 }}>
        <div className="ligne" style={{ justifyContent: 'space-between' }}>
          <span>Sous-total</span>
          <Montant valeur={totaux.sousTotal} />
        </div>
        {totaux.arrondi !== 0 && (
          <div className="ligne" style={{ justifyContent: 'space-between' }}>
            <span>Arrondi</span>
            <Montant valeur={totaux.arrondi} />
          </div>
        )}
        <div className="ligne" style={{ justifyContent: 'space-between', fontWeight: 600 }}>
          <span>Total</span>
          <Montant valeur={totaux.total} />
        </div>
      </div>
    </div>
  );
}
